import { Action, Dispatch } from 'redux';
import {
  API_DEFAULT_RETRIES,
  CES_SUBMIT_URL,
  CES,
  CTUI,
  KEVALIN_COLLECTIONS,
  CES_EVENT_CATEGORIES,
  CES_EVENT_TYPES,
} from 'appConstants';
import { ApiRequest, BrowserUtils, KevalinUtils } from 'services';
import { CESFeedback, CESFeedbackKevalin } from 'types';

export interface CesAction extends Action {
  comment?: string;
  score?: number | undefined;
  visibility?: boolean;
  successState?: boolean;
  submitSuccessSurvey?: boolean;
  _isSuccessSubmitSurvey?: boolean;
  _isErrorSubmitSurvey?: boolean;
  isLoading?: boolean;
  selectedRating?: string;
  selectedFactor?: Array<string>;
  surveyChecked?: boolean;
}

export interface RatingCES {
  readonly _isCesVisible: boolean;
  readonly _isSuccessState: boolean;
  readonly comment: string;
  readonly choice: string;
  readonly score: number | undefined;
  readonly _isSuccessSubmitSurvey: boolean;
  readonly _isErrorSubmitSurvey: boolean;
  readonly isLoading: boolean;
  readonly selectedRating: string;
  readonly selectedFactor: Array<string>;
  readonly surveyChecked?: boolean;
}

/* Initial State */

const initialState = {
  _isCesVisible: false,
  _isSuccessState: false,
  comment: '',
  choice: '',
  score: undefined,
  _isSuccessSubmitSurvey: false,
  _isErrorSubmitSurvey: false,
  isLoading: false,
  selectedRating: '',
  selectedFactor: [],
  surveyChecked: false,
} as RatingCES;

/* Actions */

enum CesActions {
  RESET_CES = 'RESET_CES',
  SET_CES_COMMENT = 'SET_CES_COMMENT',
  SET_CES_SCORE = 'SET_CES_SCORE',
  SET_CES_PANEL_VISIBILITY = 'SET_CES_MODAL_VISIBILITY',
  SET_CES_PANEL_STATE = 'SET_CES_PANEL_STATE',
  SET_SUCCESS_SUBMIT_SURVEY = 'SET_SUCCESS_SUBMIT_SURVEY',
  SET_ERROR_SUBMIT_SURVEY = 'SET_ERROR_SUBMIT_SURVEY',
  LOADING = 'LOADING',
  SET_CES_RATING = 'SET_CES_RATING',
  SET_CES_FACTOR = 'SET_CES_FACTOR',
  SET_SURVEY_CHECKED = 'SET_SURVEY_CHECKED',
}

const resetCes = (): CesAction => ({
  type: CesActions.RESET_CES,
});

const setCesComment = (comment: string): CesAction => ({
  type: CesActions.SET_CES_COMMENT,
  comment,
});

const setCesScore = (score: number): CesAction => ({
  type: CesActions.SET_CES_SCORE,
  score,
});

const showCesSurvey = (visibility: boolean): CesAction => ({
  type: CesActions.SET_CES_PANEL_VISIBILITY,
  visibility,
});

const setCesPanelState = (successState: boolean): CesAction => ({
  type: CesActions.SET_CES_PANEL_STATE,
  successState,
});

const setSucessSubmitSurveyState = (
  _isSuccessSubmitSurvey: boolean,
): CesAction => ({
  type: CesActions.SET_SUCCESS_SUBMIT_SURVEY,
  _isSuccessSubmitSurvey,
});

const setErrorSubmitSurveyState = (
  _isErrorSubmitSurvey: boolean,
): CesAction => ({
  type: CesActions.SET_ERROR_SUBMIT_SURVEY,
  _isErrorSubmitSurvey,
});

const setLoadingState = (isLoading: boolean): CesAction => ({
  type: CesActions.LOADING,
  isLoading,
});

const setSelectedRating = (selectedRating: string): CesAction => ({
  type: CesActions.SET_CES_RATING,
  selectedRating,
});

const setSelectedFactor = (selectedFactor: Array<string>): CesAction => ({
  type: CesActions.SET_CES_FACTOR,
  selectedFactor,
});

const setSurveyChecked = (surveyChecked: boolean): CesAction => ({
  type: CesActions.SET_SURVEY_CHECKED,
  surveyChecked,
});
/* Reducer */

const customerEffortSurveyReducer = (
  state = initialState,
  action: CesAction,
) => {
  switch (action.type) {
    case CesActions.SET_CES_COMMENT:
      return {
        ...state,
        comment: action.comment,
      };
    case CesActions.SET_CES_SCORE:
      return {
        ...state,
        score: action.score,
      };
    case CesActions.RESET_CES:
      return {
        ...state,
        score: undefined,
        comment: '',
        _isSuccessState: false,
      };
    case CesActions.SET_CES_PANEL_STATE:
      return {
        ...state,
        _isSuccessState: action.successState,
      };
    case CesActions.SET_CES_PANEL_VISIBILITY:
      return {
        ...state,
        _isCesVisible: action.visibility,
      };
    case CesActions.SET_SUCCESS_SUBMIT_SURVEY:
      return {
        ...state,
        _isSuccessSubmitSurvey: action._isSuccessSubmitSurvey,
      };
    case CesActions.SET_ERROR_SUBMIT_SURVEY:
      return {
        ...state,
        _isErrorSubmitSurvey: action._isErrorSubmitSurvey,
      };
    case CesActions.LOADING:
      return {
        ...state,
        isLoading: action.isLoading,
      };
    case CesActions.SET_CES_RATING:
      return {
        ...state,
        selectedRating: action.selectedRating,
      };
    case CesActions.SET_CES_FACTOR:
      return {
        ...state,
        selectedFactor: action.selectedFactor,
      };
    case CesActions.SET_SURVEY_CHECKED:
      return {
        ...state,
        surveyChecked: action.surveyChecked,
      };
    default:
      return state;
  }
};

const storeCESToKevlin = (
  ticketId: string,
  accountId: string,
  factors: Array<string>,
  rating: string,
  comment: string,
  didFailed: boolean,
) => async (dispatch: Dispatch<CesAction>) => {
  const feedback: CESFeedbackKevalin = {
    feedback: {
      ...(rating && { rating: rating }),
      ...(factors.length > 0 && { factors: factors }),
      ...(comment && { comment: comment.trim() }),
      didFailed: didFailed,
    },
  };
  const payload = {
    eventData: {
      portal: CTUI,
      surveyType: CES,
      username: window.PORTAL_DATA.username,
      domainId: window.PORTAL_DATA.domainId,
      accountType: window.PORTAL_DATA.domainId.includes('dedicated')
        ? 'dedicated'
        : 'cloud',
      accountId: accountId,
      referrer: window.location.href,
      userAgent: BrowserUtils.getBrowserDetails(),
      relatedEvents: KevalinUtils.getRelatedEvents(
        CES_EVENT_TYPES.TICKET,
        CES_EVENT_CATEGORIES.SERVICE,
        [ticketId],
        CTUI,
      ),
      ...feedback,
    },
  };
  return ApiRequest.post(
    KevalinUtils.getKevalinURL(KEVALIN_COLLECTIONS.CES),
    payload,
    {
      retries: API_DEFAULT_RETRIES,
    },
  );
};

const storeCESSurvey = (
  ticketId: string,
  accountId: string,
  factors: Array<string>,
  rating: string,
  comment: string,
) => async (dispatch: Dispatch<CesAction>) => {
  const surveyFeedback: CESFeedback = {
    feedback: {
      rating: rating,
      ...(factors.length > 0 && { factors: factors }),
      ...(comment && { comment: comment.trim() }),
    },
  };
  const payload = {
    ticketID: ticketId,
    accountID: accountId,
    ...(surveyFeedback.feedback.rating && surveyFeedback),
  };
  const onError = () => {
    dispatch(setErrorSubmitSurveyState(true));
    dispatch(
      storeCESToKevlin(ticketId, accountId, factors, rating, comment, true),
    );
  };

  const onSuccess = (response: object) => {
    if (response) {
      dispatch(setSucessSubmitSurveyState(true));
      dispatch(
        storeCESToKevlin(ticketId, accountId, factors, rating, comment, false),
      );
    }
  };
  return ApiRequest.post(CES_SUBMIT_URL, payload, {
    retries: API_DEFAULT_RETRIES,
    onError,
    onSuccess,
  });
};

export {
  CesActions,
  customerEffortSurveyReducer,
  resetCes,
  setCesComment,
  setCesScore,
  setCesPanelState,
  showCesSurvey,
  storeCESSurvey,
  storeCESToKevlin,
  setSucessSubmitSurveyState,
  setErrorSubmitSurveyState,
  setLoadingState,
  setSelectedRating,
  setSelectedFactor,
  setSurveyChecked,
};
